import { useMemo, Children, useCallback, createElement, useRef, useEffect } from 'react';
import { IconButton } from '../IconButton/IconButton.js';
import { Button } from '../Button/Button.js';
import { useLocalization } from '../Provider/Provider.js';
import { Group } from '../Group/Group.js';
import Joyride, { ACTIONS, LIFECYCLE, EVENTS } from 'react-joyride';

const Tooltip = ({
  continuous,
  index,
  isLastStep,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
  locale,
  disableTourSkipOnEsc
}) => {
  const skipButtonRef = useRef(null);
  useEffect(() => {
    const onWindowKeyDown = event => {
      if (!disableTourSkipOnEsc) {
        if (event.key === 'Escape' && skipButtonRef.current !== null) {
          event.preventDefault();
          skipButtonRef.current.click();
          return;
        }
      }
    };

    window.addEventListener('keydown', onWindowKeyDown);
    return () => {
      window.removeEventListener('keydown', onWindowKeyDown);
    };
  }, []);
  return createElement("div", Object.assign({}, tooltipProps, {
    className: "yt-tour__step w-88 relative rounded bg-white p-4"
  }), createElement(IconButton, Object.assign({}, skipProps, {
    ref: skipButtonRef,
    appearance: "discrete",
    icon: "close",
    title: locale.skip,
    "aria-label": locale.skip,
    className: "absolute top-0 right-0 mt-1 mr-1"
  })), step.title && createElement("h5", null, step.title), step.content, createElement(Group, {
    className: "mt-4 justify-end"
  }, index > 0 && createElement(Button, Object.assign({}, backProps, {
    appearance: "discrete"
  }), locale.back), createElement(Button, Object.assign({}, primaryProps, {
    appearance: "primary"
  }), continuous ? isLastStep ? `${locale.last} (${index + 1}/${size})` : `${locale.next} (${index + 1}/${size})` : locale.close)));
}; // eslint-disable-next-line @typescript-eslint/no-unused-vars


const TourStep = _props => null;
const Tour = props => {
  const {
    texts: {
      tour
    }
  } = useLocalization();
  const {
    autoStart: run,
    onComplete,
    onClose,
    onReady,
    spotlightClicks,
    disableCloseOnEsc: disableTourSkipOnEsc,
    disableScrollParentFix = false,
    scrollOffset,
    ...rest
  } = props;
  const steps = useMemo(() => {
    return Children.map(props.children, child => {
      const step = {
        disableBeacon: !child.props.showBeacon,
        target: child.props.selector,
        placement: child.props.position,
        title: child.props.title,
        content: child.props.children
      };
      return step;
    });
  }, [props.children]);
  const getStep = useCallback(selector => {
    var _props$children$find;

    return (_props$children$find = props.children.find(child => child.props.selector === selector)) === null || _props$children$find === void 0 ? void 0 : _props$children$find.props;
  }, [props.children]);

  function callback(state) {
    if (state.action === ACTIONS.SKIP && state.lifecycle === LIFECYCLE.COMPLETE) {
      if (onClose) {
        onClose(getStep(state.step.target));
      }
    }

    if (state.type === EVENTS.TOUR_END) {
      if (onComplete) {
        onComplete();
      }
    }

    if (state.lifecycle === LIFECYCLE.READY) {
      if (onReady) {
        onReady(getStep(state.step.target));
      }
    }
  }

  return createElement(Joyride, Object.assign({}, rest, {
    run: run,
    steps: steps,
    showProgress: true,
    floaterProps: {
      disableAnimation: true
    },
    tooltipComponent: tooltipProps => createElement(Tooltip, Object.assign({}, tooltipProps, {
      locale: tour,
      disableTourSkipOnEsc: disableTourSkipOnEsc
    })),
    locale: tour,
    spotlightPadding: 8,
    spotlightClicks: spotlightClicks,
    disableScrollParentFix: disableScrollParentFix,
    callback: callback,
    scrollOffset: scrollOffset,
    styles: {
      /** style beacon */
      options: {
        // tailwind.theme.colors.blue.light
        primaryColor: '#6ba4ff'
      }
    },
    disableCloseOnEsc: true
  }));
};
Tour.Step = TourStep;

export { Tour, TourStep };
//# sourceMappingURL=Tour.js.map
